import { Component, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { CommonService } from 'wire-frame';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule } from "primeng/autocomplete";
import { ConfirmationService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';

//! services
import { AppointmentService } from '../../../service/admin/appointment.service';
import { ClinicService } from '../../../service/admin/clinic.service';
import { UserService } from '../../../service/admin/user.service';
import { PatientRegistrationService } from '../../../service/admin/patient-registration.service';

import { ApiService } from '../../../service/admin/api.service';
import { ToasterService } from '../../../service/common/toaster.service';


@Component({
  selector: 'app-appointment-list',
  standalone: true,
  imports: [CommonModule, ConfirmDialogModule, FormsModule, DropdownModule, AutoCompleteModule, DialogModule, SkeletonModule],
  providers: [ConfirmationService, AppointmentService, CommonService, UserService, ClinicService, ApiService],
  templateUrl: './appointment-list.component.html',
  styleUrl: './appointment-list.component.scss'
})
export class AppointmentListComponent implements OnInit, OnDestroy {
  public checked: boolean = false;
  public showPassword = false;

  selectedPatient: any;
  public appointmentList: any[] = [];
  public patientList: any[] = [];
  public cardList: any[] = [];
  public clinicList: any[] = [];
  public doctorsList: any[] = [];

  public appointmentDialogVisible: boolean = false;
  public searchTerm: string = '';
  public selectedStatusFilter: string = 'all';
  public selectedCourierFilter: string = 'all';
  public selectedScheduleFilter: string = 'all';
  public selectedDateFilter: string = '';
  public sortField: string = 'appointment_date';
  public sortDirection: 'asc' | 'desc' = 'desc';
  public currentPage = 1;
  public pageSize = 10;
  public pageSizeOptions = [10, 20, 50];
  public totalAppointments = 0;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  public appointment: any = {
    id: 0,
    clinic_id: 1,
    doctor_id: 1,
    patient_id: 0,
    appointment_type: '',
    appointment_date: '',
    start_time: '',
    end_time: '',
    collect_later: false,
    status: '',
    notes: '',
  }

  public status = [
    {
      label: 'Scheduled',
      value: 'scheduled'
    },
    {
      label: 'Assistant Doctor',
      value: 'assistant_doctor'
    },
    {
      label: 'Doctor',
      value: 'doctor'
    },
    {
      label: 'Medicine',
      value: 'medicine'
    },
    {
      label: 'Reception',
      value: 'reception'
    },
    {
      label: 'Medicine Scheduled',
      value: 'medicine_scheduled'
    },
    {
      label: 'Completed',
      value: 'completed'
    },
    {
      label: 'Cancelled',
      value: 'cancelled'
    },
  ];

  public appointmentType: any = [
    { label: 'New', value: 'new' },
    { label: 'Follow Up', value: 'follow up' },
    { label: 'Case Paper', value: 'case' },
  ]

  timeSlots: any[] = [];
  selectedSlot: any;
  selectedDate: Date = new Date();
  private intervalId: any;

  private apiUrl: string | undefined;

  get availableSlotsCount(): number {
    return (this.timeSlots || []).filter((slot: any) => !slot?.disabled).length;
  }

  get blockedSlotsCount(): number {
    return (this.timeSlots || []).filter((slot: any) => !!slot?.disabled).length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalAppointments / this.pageSize));
  }

  get paginatedAppointments(): any[] {
    // If backend paginated, totalAppointments will likely differ from appointmentList length (unless total <= pageSize)
    // But if backend did NOT paginate, appointmentList length will be the full dataset size, and we should slice it.
    if (this.totalAppointments > this.pageSize && this.appointmentList.length > this.pageSize) {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      return this.appointmentList.slice(startIndex, startIndex + this.pageSize);
    }
    return this.appointmentList;
  }

  public isLoading: boolean = false;

  constructor(
      public common: CommonService,
      private toast: ToasterService,
      private confirmationService: ConfirmationService,
      private appointmentService: AppointmentService,
      private userService: UserService,
      private clinicService: ClinicService,
      private patientRegistrationService: PatientRegistrationService,
      private http: HttpClient, 
      private cdr: ChangeDetectorRef,
      private apiService: ApiService,
    ) {
      this.apiUrl = this.apiService.getBaseUrl();
      this.common.breadCrum = {
        title: 'Appointment Master',
        icon: 'user',
        list: ['Appointment Master'],
      };
  }

  private getAuthHeaders() {
          const token = localStorage.getItem('jwtToken');
          return {
            headers: new HttpHeaders({
              'Authorization' : `Bearer ${token}`
            })
          }
        }

  ngOnInit() {
    this.getClinic();
    this.getDoctors();
    this.getAppointments();
    this.generateTimeSlots();   // ✅ load slots on startup

    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1;
      this.getAppointments();
    });

    // ✅ Auto-refresh slots every minute
    this.intervalId = setInterval(() => {
      this.generateTimeSlots();
    }, 60 * 1000);
  }

  openAddDialog() {
    this.clearForm();
    this.selectedPatient = null;
    this.selectedSlot = null;
    this.appointmentDialogVisible = true;
  }

  openEditDialog(item: any) {
    this.appointment = { ...item };
    this.selectedSlot = null;
    this.appointmentDialogVisible = true;
  }

  confirmDelete(item: any) {
    this.confirmationService.confirm({
      header: 'Delete Appointment',
      message: `Delete appointment for ${item?.patient_name || 'this patient'}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteAppointment(item),
    });
  }

  setSort(field: string) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.getAppointments();
  }

  clearFilters() {
    this.searchTerm = '';
    this.selectedStatusFilter = 'all';
    this.selectedCourierFilter = 'all';
    this.selectedScheduleFilter = 'all';
    this.selectedDateFilter = '';
    this.currentPage = 1;
    this.getAppointments();
  }

  onFilterChange() {
    this.currentPage = 1;
    this.getAppointments();
  }

  onSearchChange(event: string) {
    this.searchTerm = event;
    this.searchSubject.next(event);
  }

  updatePageSize() {
    this.currentPage = 1;
    this.getAppointments();
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      this.getAppointments();
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
      this.getAppointments();
    }
  }

  getTypeClass(type: string): string {
    const normalized = (type || '').toLowerCase().replace(/\s+/g, '-');
    if (normalized === 'new') return 'new';
    if (normalized === 'follow-up') return 'follow-up';
    if (normalized === 'case') return 'case';
    return 'default';
  }

  getStatusClass(status: string): string {
    const normalized = (status || '').toLowerCase().replace(/[\s_]+/g, '-');
    switch (normalized) {
      case 'scheduled':
        return 'scheduled';
      case 'assistant-doctor':
        return 'assistant-doctor';
      case 'doctor':
        return 'doctor';
      case 'medicine':
        return 'medicine';
      case 'reception':
        return 'reception';
      case 'medicine-scheduled':
        return 'medicine-scheduled';
      case 'completed':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'default';
    }
  }

  // ngAfterViewInit() {
  //   setTimeout(() => this.loadEvents(), 0);
  // }

  //! This Might be the painpoint
  // loadAllDataAndAppointments() {
  //   forkJoin({
  //     clinics: this.clinicService.getClinic().pipe(catchError(() => of({ data: [] }))),
  //     patients: this.patientRegistrationService.getPatient().pipe(catchError(() => of({ data: [] }))),
  //     doctors: this.userService.getDoctors().pipe(catchError(() => of({ data: [] }))),
  //     appointments: this.appointmentService.getAppointment().pipe(catchError(() => of({ data: [] }))),
  //   }).subscribe({
  //     next: ({ clinics, patients, doctors, appointments }: any) => {
  //       this.clinicList = Array.isArray(clinics?.data)
  //         ? clinics.data.map((clinic: any) => ({
  //             label: clinic.name,
  //             value: clinic.id,
  //           }))
  //         : [];
  //
  //       this.patientList = Array.isArray(patients?.data) ? patients.data : [];
  //
  //       this.doctorsList = Array.isArray(doctors?.data)
  //         ? doctors.data.map((doctor: any) => ({
  //             label: doctor.name,
  //             value: doctor.id,
  //           }))
  //         : [];
  //
  //       this.appointmentList = this.mapAppointments(appointments?.data || []);
  //     },
  //     error: () => {
  //       this.clinicList = [];
  //       this.patientList = [];
  //       this.doctorsList = [];
  //       this.appointmentList = [];
  //     }
  //   });
  // }

  // Previous implementation kept for debugging reference:
  // getAppointments() {
  //   const needsLookupData = !this.patientList.length || !this.clinicList.length || !this.doctorsList.length;
  //
  //   const lookupRequest$ = needsLookupData
  //     ? forkJoin({
  //         clinics: this.clinicService.getClinic().pipe(catchError(() => of({ data: [] }))),
  //         patients: this.patientRegistrationService.getPatient().pipe(catchError(() => of({ data: [] }))),
  //         doctors: this.userService.getDoctors().pipe(catchError(() => of({ data: [] }))),
  //       }).pipe(
  //         tap(({ clinics, patients, doctors }: any) => {
  //           this.clinicList = Array.isArray(clinics?.data)
  //             ? clinics.data.map((clinic: any) => ({
  //                 label: clinic.name,
  //                 value: clinic.id,
  //               }))
  //             : [];
  //
  //           this.patientList = Array.isArray(patients?.data) ? patients.data : [];
  //
  //           this.doctorsList = Array.isArray(doctors?.data)
  //             ? doctors.data.map((doctor: any) => ({
  //                 label: doctor.name,
  //                 value: doctor.id,
  //               }))
  //             : [];
  //         })
  //       )
  //     : of(null);
  //
  //   lookupRequest$
  //     .pipe(switchMap(() => this.appointmentService.getAppointment()))
  //     .subscribe((res: any) => {
  //       this.appointmentList = this.mapAppointments(res?.data || []);
  //     }, () => {
  //       this.toast.add({ severity: 'error', summary: 'Error', detail: 'Error fetching appointment list' });
  //     });
  // }

  getAppointments() {
    this.isLoading = true;
    const params = {
      paginate: 'true',
      page: this.currentPage,
      per_page: this.pageSize,
      search: this.searchTerm,
      status: this.selectedStatusFilter,
      courier: this.selectedCourierFilter,
      schedule: this.selectedScheduleFilter,
      date: this.selectedDateFilter,
      sort_field: this.sortField,
      sort_direction: this.sortDirection,
      compact: 'true'
    };

    this.appointmentService.getAppointment(params).subscribe((res: any) => {
      const appointments = Array.isArray(res?.data) ? res.data : [];
      let mapped = this.mapAppointments(appointments);
      if (!res?.pagination) {
        mapped = this.applyFrontendFilters(mapped);
        this.appointmentList = mapped;
        this.totalAppointments = mapped.length;
      } else {
        this.appointmentList = mapped;
        this.totalAppointments = res?.pagination?.total || mapped.length;
      }
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
      this.toast.add({ severity: 'error', summary: 'Error', detail: 'Error fetching appointment list' });
    });
  }

  private mapAppointments(appointments: any[]) {
    return appointments.map((appointment: any) => {
      const patient = this.patientList.find(p => p.id === appointment.patient_id);
      const clinic = this.clinicList.find(c => c.value === appointment.clinic_id);
      const doctor = this.doctorsList.find(d => d.value === appointment.doctor_id);
      const rawCourier = appointment?.is_courier;
      const normalizedCourier = rawCourier === null || rawCourier === undefined ? '' : String(rawCourier).trim().toLowerCase();
      const isCourierFlag = rawCourier === true || rawCourier === 1 || normalizedCourier === '1' || normalizedCourier === 'yes' || normalizedCourier === 'y' || normalizedCourier === 'courier' || normalizedCourier === 'nri';
      const rawCollectLater = appointment?.prescription_collect_later ?? appointment?.collect_later;
      const isScheduledLater = rawCollectLater === true || rawCollectLater === 1 || String(rawCollectLater || '').trim().toLowerCase() === '1' || String(rawCollectLater || '').trim().toLowerCase() === 'true';

      const patientNameFromPayload =
        appointment?.patient_name ||
        [appointment?.patient?.first_name, appointment?.patient?.middle_name, appointment?.patient?.last_name]
          .filter((x: any) => !!x)
          .join(' ')
          .trim();

      const doctorNameFromPayload =
        appointment?.doctor_name ||
        [appointment?.doctor?.first_name, appointment?.doctor?.last_name]
          .filter((x: any) => !!x)
          .join(' ')
          .trim();

      const clinicNameFromPayload = appointment?.clinic_name || appointment?.clinic?.name || '';

      return {
        ...appointment,
        appointment_id: appointment.id,
        start_time: appointment.start_time,
        patient_name: patientNameFromPayload || (patient ? (patient.first_name + ' ' + (patient.middle_name || '') + ' ' + patient.last_name).trim() : ''),
        clinic_name: clinicNameFromPayload || (clinic ? clinic.label : ''),
        doctor_name: doctorNameFromPayload || (doctor ? doctor.label : ''),
        isCourier: isCourierFlag ? 'Courier' : 'OPD',
        collectLater: isScheduledLater,
        scheduledLater: isScheduledLater ? 'Scheduled' : 'Normal',
      };
    });
  }

  private applyFrontendFilters(list: any[]) {
    let filteredList = list;

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      filteredList = filteredList.filter(item => 
        (item.patient_name || '').toLowerCase().includes(term) ||
        (item.doctor_name || '').toLowerCase().includes(term) ||
        (item.clinic_name || '').toLowerCase().includes(term) ||
        (item.status || '').toLowerCase().includes(term) ||
        (item.appointment_type || '').toLowerCase().includes(term)
      );
    }
    
    if (this.selectedStatusFilter && this.selectedStatusFilter !== 'all') {
      filteredList = filteredList.filter(item => item.status === this.selectedStatusFilter);
    }
    
    if (this.selectedDateFilter) {
      filteredList = filteredList.filter(item => item.appointment_date === this.selectedDateFilter);
    }

    if (this.selectedCourierFilter && this.selectedCourierFilter !== 'all') {
      if (this.selectedCourierFilter === 'MC') {
        filteredList = filteredList.filter(item => item.collectLater);
      } else if (this.selectedCourierFilter === 'Courier') {
        filteredList = filteredList.filter(item => item.isCourier === 'Courier');
      } else if (this.selectedCourierFilter === 'OPD') {
        filteredList = filteredList.filter(item => item.isCourier !== 'Courier');
      }
    }

    if (this.selectedScheduleFilter && this.selectedScheduleFilter !== 'all') {
      if (this.selectedScheduleFilter === 'Scheduled') {
        filteredList = filteredList.filter(item => item.collectLater);
      } else {
        filteredList = filteredList.filter(item => !item.collectLater);
      }
    }

    return filteredList;
  }

  getPatient(callback?: () => void) {
    this.patientRegistrationService.getPatient().subscribe((res: any) => {
      this.patientList = res.data || [];
      if (callback) callback();
    },() => {
      this.patientList = [];
      if (callback) callback();
    });
  }


  getClinic(callback?: () => void) {
    this.clinicService.getClinic().subscribe({
      next: (res: any) => {
        this.clinicList = Array.isArray(res.data)
          ? res.data.map((clinic:any) => ({
              label: clinic.name,
              value: clinic.id
            }))
          : [];
        if (callback) callback();
      },
      error: (err) => {
        console.error('Clinic API error:', err);
        this.clinicList = [];
        if (callback) callback();
      }
    });
  }

  getDoctors(callback?: () => void) {
    this.userService.getDoctors().subscribe({
      next: (res: any) => {
        this.doctorsList = Array.isArray(res.data)
          ? res.data.map((doctor:any) => ({
              label: doctor.name,
              value: doctor.id
            }))
          : [];
        if (callback) callback();
      },
      error: (err) => {
        console.error('Doctor API error:', err);
        this.doctorsList = [];
        if (callback) callback();
      }
    });
  }

  closeForm() {
    this.clearForm();
    this.selectedPatient = null;
    this.selectedSlot = null;
    this.appointmentDialogVisible = false;
  }

  clearForm() {
    this.appointment = {
      id: 0,
      clinic_id: 0,
      doctor_id: 0,
      patient_id: 0,
      appointment_type: '',
      appointment_date: '',
      start_time: '',
      end_time: '',
      collect_later: false,
      status: '',
      notes: '',

      patient: {
        id: 0,
        clinic_id: 0,
        card_id: 0,
        patient_number: '',
        salutation: '',
        full_name: '',
        dob: '',
        age: 0,
        contact_number: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        country: '',
        occupation: '',
        referred_by: '',
        referred_by_dr: '',
        is_free: false,
        is_active: true,
        first_visit: '',

        prescriptions: {
          id: 0,
          appointment_id: 6,
          summary: '',
          medicines: '',
          chief_complaints: '',
          diagnosis: '',
          past_history: '',
          menstrual_history: '',
          obstetric_history: '',
          respiratory_system: '',
          cardiovascular_system: '',
          central_nervous_system: '',
          per_abdomen: '',

          family_history: {
            mother: '',
            father: '',
            other: ''
          },

          others: {
            nail: '',
            mentals: '',
            stool: '',
            urine: '',
            sleep: '',
            parsp: '',
            corn: '',
            mole: '',
            hair: '',
            spine: '',
            pallor: '',
            cyanosis: '',
            clubbing: '',
            icterus: '',
            thirst: '',
            craving: '',
            aversion: '',
            dreams: '',
            thermals: '',
            warts: '',
            lm: '',
            tongue: ''
          }
        }
      }
    };
  }

  processAppointments() {
    if (this.appointment?.id) {
      this.updateAppointment();
    } else {
      this.addAppointment();
    }
  }

  addAppointment() {
    this.appointmentService
      .addAppointment(this.appointment)
      .subscribe((res) => {
        this.toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Appointment Created Successfuly',
        });
        this.closeForm();
        this.getAppointments();
      });
  }

  updateAppointment() {
    this.appointmentService
      .updateAppointment(this.appointment)
      .subscribe((res) => {
        this.toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Appointment updated Successfuly.',
        });
        this.closeForm();
        this.getAppointments();
      });
  }

  deleteAppointment(item: any) {
    // console.log('We are deleting this item', item);
    this.appointmentService.deleteAppointment(item).subscribe((res: any) => {
      this.toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Appointment deleted Successfuly.',
      });
      this.closeForm();
      this.getAppointments();
    });
  }

  onPatientSelect(event: any) {
  // event.value is the selected patient object
    this.appointment.patient_id = event.value.id || event.value.patient_id;
    // Optionally, store the whole patient object if needed
    this.selectedPatient = event.value;
    // console.log('Selected patient_id:', this.appointment.patient_id);
  }

  searchPatients(event: any) {
    const query = event.query;
    this.http.get<any>(this.apiUrl + '/patient/search', { params: { q: query }, ...this.getAuthHeaders()})
      .subscribe(res => {
        if (!res.data || res.data.length === 0) {
          this.patientList = [];
          this.toast.add({
            severity: 'info',
            summary: 'No patients found',
            detail: `No patients found for "${query}".`
          });
        } else {
          this.patientList = res.data.map((p: any) => ({
            ...p,
            full_name: `${p.first_name} ${p.last_name} (${p.patient_number})`
          }));
        }
        // Stop spinner and update view
        this.cdr.detectChanges();
      }, error => {
        this.patientList = [];
        this.toast.add({
          severity: 'info',
          summary: 'Info',
          detail: 'No patients found.'
        });
        this.cdr.detectChanges();
      });
  }

  //! ========================= Time Slots Code ================================
  ngOnDestroy(): void {
    // clear interval when component is destroyed
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  // called whenever user changes the date
  onDateChange(date: string) {
    this.appointment.appointment_date = date;
    this.generateTimeSlots();
  }

  generateTimeSlots() {
    const slots = [];
    const now = new Date();
    const startHour = 9;
    const endHour = 23; // 9 PM

    for (let hour = startHour; hour < endHour; hour++) {
      const start = this.formatTime(hour);
      const end = this.formatTime(hour + 1);

      const slotEnd = new Date();
      slotEnd.setHours(hour + 1, 0, 0, 0);

      slots.push({
        label: `${this.formatAmPm(hour)} - ${this.formatAmPm(hour + 1)}`,
        start,
        end,
        disabled: slotEnd <= now   // ✅ disable if the slot has already ended
      });
    }

    if (this.appointment?.appointment_date) {
      const [year, month, day] = this.appointment.appointment_date.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day);

      const today = new Date();

      // If selected date is before today → disable all slots
      if (selectedDate < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
        slots.forEach(slot => slot.disabled = true);
      }
      // If selected date is after today → enable all slots
      else if (selectedDate > new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
        slots.forEach(slot => slot.disabled = false);
      }
      // If selected date is today → keep your default slotEnd <= now logic
    }

    this.timeSlots = slots;
  }

    // generateTimeSlots() {
    //   const slots = [];
    //   const startHour = 9;
    //   const endHour = 21; // 9 PM

    //   // Parse selected appointment date (if any)
    //   let selectedDate: Date | null = null;
    //   if (this.appointment?.appointment_date) {
    //     const [year, month, day] = this.appointment.appointment_date.split('-').map(Number);
    //     selectedDate = new Date(year, month - 1, day);
    //   }

    //   const today = new Date();
    //   const isToday =
    //     selectedDate &&
    //     selectedDate.toDateString() === today.toDateString();

    //   for (let hour = startHour; hour < endHour; hour++) {
    //     const start = this.formatTime(hour);
    //     const end = this.formatTime(hour + 1);

    //     let disabled = false;

    //     // Disable slots only if it's for today and the time has already passed
    //     if (isToday) {
    //       const slotEnd = new Date();
    //       slotEnd.setHours(hour + 1, 0, 0, 0);
    //       disabled = slotEnd <= today;
    //     }

    //     slots.push({
    //       label: `${this.formatAmPm(hour)} - ${this.formatAmPm(hour + 1)}`,
    //       start,
    //       end,
    //       disabled,
    //     });
    //   }

    //   // If date is before today → disable all
    //   if (selectedDate && selectedDate < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
    //     slots.forEach(s => (s.disabled = true));
    //   }

    //   this.timeSlots = slots;
    // }

  // formatTime(hour: number): string {
  //   return `${hour.toString().padStart(2, '0')}:00`;
  // }

  formatTime(value: any): string {
    // If value is already "HH:MM" or "HH:MM:SS" → return clean HH:MM
    if (typeof value === 'string') {
      const parts = value.split(':');  // ["01", "00", "00"]
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }

    // If value is a number (hour only)
    if (typeof value === 'number') {
      return `${value.toString().padStart(2, '0')}:00`;
    }

    // If value is a Date object
    if (value instanceof Date) {
      const hours = value.getHours().toString().padStart(2, '0');
      const minutes = value.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    return '';
  }


  formatAmPm(hour: number): string {
    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = ((hour + 11) % 12 + 1);
    return `${displayHour}:00 ${suffix}`;
  }

  selectSlot(slot: any) {
    // console.log('Selected slot:', slot);
    if (slot.disabled) return; // prevent picking disabled slots

    this.selectedSlot = slot;
    this.appointment.start_time = slot.start;
    this.appointment.end_time = slot.end;
  }

}
